// Board statuses (06 §Boards and tasks; 02 `board.status_set`). Adapted from Work OS
// `src/domain/containers.ts` (status sets, `matchStatus` :266-283, `alignTaskStatus` :416-438)
// and `src/domain/tasks.ts` (completion is a timestamp, the status a label: `completeTask`
// :191-257, `reopenTask` :262-290), with Paperclip's status side effects
// (`server/src/services/issues.ts` :331-347: entering done sets `completedAt`, leaving clears it).
// Work OS inherits sets down a container tree; a CompanyOS board owns one set (the space's default
// is copied at creation), so there is no inheritance here.

/**
 * BOARD-GATES (docs/design/2026-11-02-board-stages-gates-kpis.md §2.1): only a person moves a
 * card out of this column, with Approve · Rework · Reject (each a target column).
 */
export type ColumnGate = {
  needs_person: true;
  approve_to: string;
  rework_to: string;
  reject_to: string;
  approver?: "owner" | { seat_id: string } | undefined;
};

export type StatusDef = {
  key: string;
  name: string;
  done: boolean;
  started?: boolean | undefined;
  color?: string | undefined;
  gate?: ColumnGate | undefined;
};

/** The column's person-only gate, if it has one. */
export function columnGateOf(set: readonly StatusDef[], key: string): ColumnGate | null {
  const g = statusOf(set, key)?.gate;
  return g?.needs_person === true ? g : null;
}

/** The set a board gets when neither the request nor its space names one. */
export const DEFAULT_STATUS_SET: readonly StatusDef[] = [
  { key: "todo", name: "To do", done: false },
  { key: "in_progress", name: "In progress", done: false, started: true },
  { key: "done", name: "Done", done: true },
];

export function statusOf(set: readonly StatusDef[], key: string): StatusDef | undefined {
  return set.find((s) => s.key === key);
}

/** `POST tasks` without a status: the board's first status (04 `TaskCreate.status`). */
export function defaultStatus(set: readonly StatusDef[]): StatusDef {
  const first = set[0];
  if (!first) throw new Error("a status set has at least one status");
  return first;
}

export function isDone(set: readonly StatusDef[], key: string): boolean {
  return statusOf(set, key)?.done === true;
}

/**
 * Whether open hard dependencies block entering `key` (06: "Hard dependencies block moving to
 * `in_progress`/done statuses"): a status marked `started` or `done`.
 */
export function blockedByHardDependencies(set: readonly StatusDef[], key: string): boolean {
  const s = statusOf(set, key);
  return s !== undefined && (s.done || s.started === true);
}

/**
 * `completed_at` after a status change. Entering a done status from an open one stamps `now`;
 * moving between done statuses keeps the original stamp (Work OS: completing again only
 * relabels); leaving done clears it.
 */
export function completedAtAfter(
  set: readonly StatusDef[],
  to: string,
  completedAt: Date | null,
  now: Date,
): Date | null {
  if (!isDone(set, to)) return null;
  return completedAt ?? now;
}

/**
 * The status a task takes in another set (a move to another board, 04 `TaskMove`): Work OS
 * `matchStatus` — the pool is the target's done statuses when the task is complete, else its open
 * ones (the whole set when that pool is empty); within the pool the same key wins, then the same
 * name (case-insensitive), then the pool's first status.
 */
export function mapStatus(
  from: readonly StatusDef[],
  to: readonly StatusDef[],
  key: string,
): StatusDef {
  const done = isDone(from, key);
  const name = statusOf(from, key)?.name.toLowerCase();
  const matching = to.filter((s) => s.done === done);
  const pool = matching.length > 0 ? matching : to;
  return (
    pool.find((s) => s.key === key) ??
    (name === undefined ? undefined : pool.find((s) => s.name.toLowerCase() === name)) ??
    defaultStatus(pool)
  );
}

/** Keys of `before` that `after` no longer has (a removed status may not hold tasks: 409). */
export function removedStatusKeys(
  before: readonly StatusDef[],
  after: readonly StatusDef[],
): string[] {
  const kept = new Set(after.map((s) => s.key));
  return before.filter((s) => !kept.has(s.key)).map((s) => s.key);
}

/** The structural rules `StatusSet` (contracts) states, for callers outside the schema. */
export function statusSetProblems(set: readonly StatusDef[]): string[] {
  const problems: string[] = [];
  if (set.length === 0) problems.push("at least one status");
  if (new Set(set.map((s) => s.key)).size !== set.length)
    problems.push("status keys must be unique");
  if (!set.some((s) => s.done)) problems.push("at least one status must be done");
  return problems;
}
